import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Scissors } from "lucide-react";
import Segmented from "@/components/enhance/Segmented";
import { GROUP_COLORS, STEM_CHOICES, splitSong } from "@/lib/exportAudioApi";
import { safeApiError } from "@/lib/safeErrorText";

function SplitCard({ songId, split, splitting, refresh }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await splitSong(songId); await refresh(); } catch (e) { toast.error("Couldn't start splitting", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="export-split" className="rounded-2xl border border-cyan-300/20 bg-cyan-400/[0.05] p-4">
      <p className="text-[13px] text-white/75">This song hasn't been split into parts yet — split it to export stems (vocals, drums, bass, piano, guitar, other).</p>
      {split?.status === "error" ? <p data-testid="export-split-error" className="mt-2 flex items-center gap-2 text-[12px] text-rose-200"><AlertTriangle className="h-3.5 w-3.5" /> {split.error}</p> : null}
      {splitting ? (
        <p data-testid="export-split-progress" className="mt-3 flex items-center gap-2 text-[13px] text-cyan-100"><Loader2 className="h-4 w-4 animate-spin" /> Splitting into parts — usually 1–3 minutes…</p>
      ) : (
        <button type="button" data-testid="export-split-start" onClick={go} disabled={busy}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-cyan-300 px-4 py-2 text-[13px] font-semibold text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />} {split?.status === "error" ? "Split again" : "Split into parts"}
        </button>
      )}
    </div>
  );
}

function Groups({ set, value }) {
  const showGroups = value === "groups" || value === "both";
  const showAll = value === "all" || value === "both";
  const label = (n) => set.stems.find((s) => s.name === n)?.label || n;
  const partsOf = (g) => g.parts.map(label).join(" + ");
  return (
    <div data-testid="export-stems-preview" className="space-y-2 rounded-2xl border border-white/[0.06] bg-black/25 p-3.5 text-[12px]">
      {showGroups ? <p className="text-white/45">Grouped into Lead · Background vocals · Instrumental:</p> : null}
      {showGroups ? set.groups.map((g) => (
        <div key={g.key} data-testid={`export-group-${g.key}`} className="flex items-start gap-2.5">
          <span className="mt-1 h-3 w-1.5 flex-shrink-0 rounded-full" style={{ background: GROUP_COLORS[g.key] }} />
          <span><span className="font-semibold text-white/85">{g.label}</span>{partsOf(g) !== g.label ? <span className="text-white/45"> — {partsOf(g)}</span> : null}</span>
        </div>
      )) : null}
      {showAll ? <p data-testid="export-every-part" className="text-white/55"><span className="font-semibold text-white/85">Every part:</span> {set.stems.map((s) => s.label).join(" · ")}</p> : null}
      {value === "none" ? <p className="text-white/45">Only the full mix goes in the pack.</p> : null}
    </div>
  );
}

/** Stems: grouped Lead · BGV · Instrumental, every part, both or none — or split the song first. */
export default function StemsPicker({ detail, set, onSet, value, onChange, splitting, refresh }) {
  return (
    <div className="space-y-3">
      <h3 className="font-display text-[15px] font-semibold">Stems</h3>
      {!detail.sets.length ? <SplitCard songId={detail.song.id} split={detail.split} splitting={splitting} refresh={refresh} /> : (
        <>
          {detail.sets.length > 1 ? <Segmented label="Parts to use" value={set?.id} options={detail.sets.map((s) => [s.id, s.label])} onChange={onSet} testId="export-set" /> : null}
          <Segmented label="Stems in the pack" value={value} options={STEM_CHOICES} onChange={onChange} testId="export-stems" />
          {set ? <Groups set={set} value={value} /> : null}
        </>
      )}
    </div>
  );
}
